import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { IconAlertTriangle, IconChevronDown, IconExternalLink } from '@tabler/icons-react';
import toast from 'react-hot-toast';
import { apiClient } from '../../../config/apiConfig';
import { useAuth } from '../../../contexts/auth/AuthContext';
import { getSafeExternalUrl } from '../../../utils/safeExternalUrl';
import { useDirtyNavigationGuard } from '../../routing/DirtyNavigationGuard';

const STATE_LABELS = {
  not_required: 'Not required', missing: 'Missing', linked: 'Linked', draft: 'Draft', ready: 'Ready', sent: 'Sent',
  paid_declared: 'Paid declared', partially_received: 'Part received', received: 'Received',
  not_started: 'Not started', live: 'Live', unavailable: 'Unavailable'
};

const stateLabel = (state) => STATE_LABELS[state] || String(state || 'Unavailable').replaceAll('_', ' ');

const moneyLabel = (money) => {
  if (!money || typeof money !== 'object') return 'Unavailable';
  if (typeof money.currency === 'string' && Number.isFinite(Number(money.amount))) {
    return `${money.currency} ${Number(money.amount).toLocaleString()}`;
  }
  const entries = Object.entries(money).filter(([, amount]) => Number.isFinite(Number(amount)));
  if (!entries.length) return 'Unavailable';
  return entries.map(([currency, amount]) => `${currency} ${Number(amount).toLocaleString()}`).join(' · ');
};

const withCoverage = (value, coverage) => coverage === 'partial'
  ? `${value} (partial)`
  : coverage === 'unavailable' ? 'Unavailable' : value;

const Fact = ({ label, children }) => (
  <div className="min-w-0 border-l-2 border-glass-border pl-3">
    <div className="text-[10px] font-bold uppercase tracking-[0.08em] text-text-tertiary">{label}</div>
    <div className="mt-1 text-sm text-text-primary">{children}</div>
  </div>
);

export default function CampaignOperationsSummary({ summary, campaignId, onRefresh, loading = false, error = '', onRetry }) {
  const { hasPermission } = useAuth();
  const canManageBrief = hasPermission(['manage_campaigns', 'manage_briefs']);
  const canManagePayments = hasPermission('manage_payments');
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [briefEditing, setBriefEditing] = useState(false);
  const [briefUrl, setBriefUrl] = useState('');
  const [briefState, setBriefState] = useState('draft');
  const [savingBrief, setSavingBrief] = useState(false);
  const briefSubmittingRef = useRef(false);
  const [briefError, setBriefError] = useState('');
  const [editingAgreement, setEditingAgreement] = useState(null);
  const [savingAgreement, setSavingAgreement] = useState(false);
  const savedBriefUrl = summary?.brief?.url || '';
  const savedBriefState = summary?.brief?.state === 'ready' || summary?.brief?.state === 'sent' ? 'ready' : 'draft';
  const resetBriefEditor = useCallback(() => {
    setBriefUrl(savedBriefUrl);
    setBriefState(savedBriefState);
    setBriefError('');
    setBriefEditing(false);
  }, [savedBriefState, savedBriefUrl]);
  const {
    setDirty: setBriefDirty,
    setInFlight: setBriefInFlight,
    release: releaseBriefGuard,
  } = useDirtyNavigationGuard(`campaign-brief-${campaignId}`, { onDiscard: resetBriefEditor });
  const briefIsDirty = briefEditing && (briefUrl !== savedBriefUrl || briefState !== savedBriefState);

  useEffect(() => {
    setBriefDirty(briefIsDirty);
  }, [briefIsDirty, setBriefDirty]);

  useEffect(() => {
    if (briefEditing) return;
    setBriefUrl(summary?.brief?.url || '');
    setBriefState(summary?.brief?.state === 'ready' || summary?.brief?.state === 'sent' ? 'ready' : 'draft');
  }, [briefEditing, summary]);

  const invoice = summary?.finance?.invoice;
  const creators = summary?.finance?.creators;
  const profit = summary?.finance?.profit;
  const coverage = summary?.finance?.coverage || {};
  const firstInvoice = invoice?.refs?.[0];
  const invoiceHref = firstInvoice?.href || (firstInvoice?.id
    ? `/ops/invoices?invoiceId=${encodeURIComponent(firstInvoice.id)}`
    : `/ops/invoices?action=create&campaignId=${encodeURIComponent(campaignId)}`);
  const distribution = useMemo(() => {
    if (summary?.brief?.distribution) return stateLabel(summary.brief.distribution);
    const sent = summary?.brief?.groupsSent;
    const total = summary?.brief?.groupsTotal;
    return Number.isFinite(sent) && Number.isFinite(total) && total > 0 ? `${sent}/${total} sent` : 'Not distributed';
  }, [summary]);
  const safeBriefUrl = getSafeExternalUrl(summary?.brief?.url);

  if (!summary) {
    return (
      <section className="mb-6 border border-glass-border bg-surface-1" aria-labelledby="campaign-operations-title">
        <div className="p-4">
          <h2 id="campaign-operations-title" className="font-semibold text-text-primary">Operations summary</h2>
          {loading ? (
            <p className="mt-2 text-sm text-text-secondary" role="status">Loading Campaign readiness…</p>
          ) : (
            <div className="mt-2 flex flex-wrap items-center justify-between gap-3" role="alert">
              <p className="text-sm text-error">{error || 'Campaign readiness is unavailable.'}</p>
              {onRetry && <button type="button" onClick={onRetry} className="min-h-11 border border-error px-3 text-sm text-error">Retry readiness</button>}
            </div>
          )}
        </div>
      </section>
    );
  }

  const saveBrief = async () => {
    if (briefSubmittingRef.current) return;
    briefSubmittingRef.current = true;
    try {
      setBriefInFlight(true);
      setSavingBrief(true);
      setBriefError('');
      await apiClient.put(`/agency/campaigns/${campaignId}/brief`, { url: briefUrl.trim(), state: briefState });
      toast.success(briefState === 'ready' ? 'Brief marked ready' : 'Brief draft saved');
      releaseBriefGuard();
      setBriefEditing(false);
      if (onRefresh) {
        try {
          await onRefresh();
        } catch {
          // The mounted summary owns refresh error feedback; the brief itself was saved.
        }
      }
    } catch (error) {
      const message = error.response?.data?.error || 'Could not save brief';
      setBriefError(message);
      toast.error(message);
    } finally {
      briefSubmittingRef.current = false;
      setSavingBrief(false);
      setBriefInFlight(false);
    }
  };

  const editBrief = () => {
    setBriefUrl(summary.brief?.url || '');
    setBriefState(summary.brief?.state === 'ready' || summary.brief?.state === 'sent' ? 'ready' : 'draft');
    setBriefError('');
    setBriefEditing(true);
  };

  const cancelBriefEdit = () => {
    if (briefSubmittingRef.current || savingBrief) return;
    releaseBriefGuard();
    setBriefUrl(summary.brief?.url || '');
    setBriefState(summary.brief?.state === 'ready' || summary.brief?.state === 'sent' ? 'ready' : 'draft');
    setBriefError('');
    setBriefEditing(false);
  };

  const saveAgreement = async () => {
    try {
      setSavingAgreement(true);
      await apiClient.put(`/agency/campaigns/${campaignId}/creator-entries/${editingAgreement.entryId}/agreement`, {
        amount: Number(editingAgreement.amount),
        currency: editingAgreement.currency.trim().toUpperCase()
      });
      toast.success('Creator agreement saved');
      setEditingAgreement(null);
      await onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not save creator agreement');
    } finally {
      setSavingAgreement(false);
    }
  };

  return (
    <section className="mb-6 border border-glass-border bg-surface-1" aria-labelledby="campaign-operations-title">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
        <div className="flex items-center gap-3">
          <h2 id="campaign-operations-title" className="font-semibold text-text-primary">Operations summary</h2>
          {summary.qualityIssues?.length > 0 && <span className="inline-flex items-center gap-1 text-xs text-warning"><IconAlertTriangle size={14} />{summary.qualityIssues.length} issue{summary.qualityIssues.length === 1 ? '' : 's'}</span>}
          {loading && <span className="text-xs text-text-tertiary" role="status">Refreshing…</span>}
        </div>
        <button type="button" className="flex min-h-11 items-center gap-2 px-2 text-sm text-accent" onClick={() => setDetailsOpen((value) => !value)} aria-expanded={detailsOpen}>
          {detailsOpen ? 'Hide operations details' : 'Show operations details'}
          <IconChevronDown size={18} className={detailsOpen ? 'rotate-180' : ''} />
        </button>
      </div>

      {error && <div className="flex flex-wrap items-center justify-between gap-3 border-t border-error/30 px-4 py-3" role="alert"><p className="text-sm text-error">{error}</p>{onRetry && <button type="button" onClick={onRetry} className="min-h-11 border border-error px-3 text-sm text-error">Retry readiness</button>}</div>}

      <div className="grid grid-cols-2 gap-px border-t border-glass-border bg-glass-border md:grid-cols-4">
        <div className="bg-surface-1 p-3"><div className="text-[10px] font-bold uppercase tracking-[0.08em] text-text-tertiary">Brief</div><div className="mt-1 text-sm font-medium text-text-primary">{stateLabel(summary.brief?.state)}</div><div className="text-xs text-text-secondary">{distribution}</div></div>
        <div className="bg-surface-1 p-3"><div className="text-[10px] font-bold uppercase tracking-[0.08em] text-text-tertiary">Invoice</div><div className="mt-1 text-sm font-medium text-text-primary">{stateLabel(invoice?.state)}</div>{coverage.invoice === 'partial' && <div className="text-xs text-warning">Partial coverage</div>}</div>
        <div className="bg-surface-1 p-3"><div className="text-[10px] font-bold uppercase tracking-[0.08em] text-text-tertiary">Payouts</div>{summary.finance === null ? <div className="mt-1 text-sm text-text-secondary">Restricted</div> : <><div className="mt-1 text-sm font-medium text-text-primary">{creators?.readyCount || 0} ready · {creators?.paidCount || 0} paid</div><div className={creators?.blockedCount ? 'text-xs text-warning' : 'text-xs text-text-secondary'}>{creators?.blockedCount || 0} blocked</div></>}</div>
        <div className="bg-surface-1 p-3"><div className="text-[10px] font-bold uppercase tracking-[0.08em] text-text-tertiary">Report</div><div className="mt-1 text-sm font-medium text-text-primary">{stateLabel(summary.report?.state)}</div></div>
      </div>

      <div className="grid grid-cols-2 gap-px border-t border-glass-border bg-glass-border md:grid-cols-4">
        <div className="bg-surface-1 p-3"><div className="text-[10px] font-bold uppercase tracking-[0.08em] text-text-tertiary">Issued</div><div className="mt-1 text-sm text-text-primary">{summary.finance === null ? 'Restricted' : withCoverage(moneyLabel(invoice?.issued), coverage.invoice)}</div></div>
        <div className="bg-surface-1 p-3"><div className="text-[10px] font-bold uppercase tracking-[0.08em] text-text-tertiary">Received</div><div className="mt-1 text-sm text-text-primary">{summary.finance === null ? 'Restricted' : withCoverage(moneyLabel(invoice?.received), coverage.invoice)}</div></div>
        <div className="bg-surface-1 p-3"><div className="text-[10px] font-bold uppercase tracking-[0.08em] text-text-tertiary">Creator owed</div><div className="mt-1 text-sm text-text-primary">{summary.finance === null ? 'Restricted' : withCoverage(moneyLabel(creators?.owed), coverage.creatorOwed)}</div></div>
        <div className="bg-surface-1 p-3"><div className="text-[10px] font-bold uppercase tracking-[0.08em] text-text-tertiary">Cash contribution</div><div className="mt-1 text-sm text-text-primary">{summary.finance === null ? 'Restricted' : withCoverage(moneyLabel(profit?.cash), coverage.cash)}</div></div>
      </div>

      {detailsOpen && (
        <div className="border-t border-glass-border p-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Fact label="Brief actions">
              <div className="flex flex-wrap gap-3">{safeBriefUrl && <a href={safeBriefUrl} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-11 items-center gap-1 text-accent">Open brief <IconExternalLink size={14} /></a>}{canManageBrief && summary.brief?.state !== 'not_required' && !briefEditing && <button type="button" onClick={editBrief} className="min-h-11 text-accent">Edit brief</button>}</div>
              {briefEditing && <div className="mt-2 flex flex-col gap-2">
                <label className="text-xs text-text-secondary">Brief URL<input aria-label="Brief URL" type="url" value={briefUrl} onChange={(event) => setBriefUrl(event.target.value)} className="mt-1 min-h-11 w-full border border-glass-border bg-surface-0 px-3 text-sm" /></label>
                <label className="text-xs text-text-secondary">Readiness<select aria-label="Brief readiness" value={briefState} onChange={(event) => setBriefState(event.target.value)} className="mt-1 min-h-11 w-full border border-glass-border bg-surface-0 px-2 text-sm"><option value="draft">Draft</option><option value="ready">Ready</option></select></label>
                {briefError && <p className="text-sm text-error" role="alert">{briefError}</p>}
                <div className="flex gap-2"><button type="button" disabled={savingBrief || !briefUrl.trim()} onClick={saveBrief} className="min-h-11 bg-accent px-3 text-sm font-medium text-black disabled:opacity-50">{savingBrief ? 'Saving…' : 'Save brief'}</button><button type="button" disabled={savingBrief} onClick={cancelBriefEdit} className="min-h-11 px-3 text-sm text-text-secondary">Cancel brief edit</button></div>
              </div>}
            </Fact>
            <Fact label="Invoice actions"><Link className="inline-flex min-h-11 items-center gap-1 text-accent" to={invoiceHref}>{firstInvoice ? `Open ${firstInvoice.ref || 'invoice'}` : 'Set up invoice'} <IconExternalLink size={14} /></Link></Fact>
            <Fact label="Payout actions">{summary.finance === null ? <span className="text-text-secondary">Restricted</span> : <div className="flex flex-wrap gap-3"><Link className="inline-flex min-h-11 items-center text-accent" to={`/payments?campaignId=${encodeURIComponent(campaignId)}`}>Open payments</Link><Link className="inline-flex min-h-11 items-center text-accent" to={`/payments/history?campaignId=${encodeURIComponent(campaignId)}`}>History</Link></div>}</Fact>
          </div>
          {summary.finance && <div className="mt-4 text-sm text-text-secondary">Forecast contribution: {withCoverage(moneyLabel(profit?.forecast), coverage.forecast)}</div>}
          {summary.finance && creators?.rows?.length > 0 && (
            <details className="mt-4 border border-glass-border p-3">
              <summary className="cursor-pointer text-sm font-medium text-text-primary">Creator payment details ({creators.rows.length})</summary>
              <div className="mt-3 space-y-2">
                {creators.rows.map((row) => {
                  const canEdit = canManagePayments && row.status !== 'PAID' && !(row.paymentIds || []).length && (row.blockers || []).length > 0;
                  return (
                    <div key={row.entryId} className="border-t border-glass-border pt-2 text-sm">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div><span className="font-medium text-text-primary">{row.name}</span><span className="ml-2 text-text-secondary">Agreed {row.amount == null || row.currency === 'UNKNOWN' ? 'Unavailable' : `${row.currency} ${Number(row.amount).toLocaleString()}`} · Owed {row.owed == null ? 'Unavailable' : `${row.currency} ${Number(row.owed).toLocaleString()}`}</span></div>
                        {canEdit && <button type="button" className="min-h-11 text-accent" onClick={() => setEditingAgreement({ entryId: row.entryId, amount: row.amount ?? '', currency: row.currency === 'UNKNOWN' ? '' : row.currency })}>Edit agreement</button>}
                      </div>
                      {editingAgreement?.entryId === row.entryId && (
                        <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-[1fr_120px_auto]">
                          <input aria-label={`Agreement amount for ${row.name}`} type="number" min="0" step="0.01" value={editingAgreement.amount} onChange={(event) => setEditingAgreement((value) => ({ ...value, amount: event.target.value }))} className="min-h-11 border border-glass-border bg-surface-0 px-3" />
                          <input aria-label={`Agreement currency for ${row.name}`} value={editingAgreement.currency} onChange={(event) => setEditingAgreement((value) => ({ ...value, currency: event.target.value }))} placeholder="USD" className="min-h-11 border border-glass-border bg-surface-0 px-3 uppercase" />
                          <div className="flex gap-2"><button type="button" disabled={savingAgreement || editingAgreement.amount === '' || !editingAgreement.currency.trim()} onClick={saveAgreement} className="min-h-11 bg-accent px-3 text-black disabled:opacity-50">Save agreement</button><button type="button" onClick={() => setEditingAgreement(null)} className="min-h-11 px-3 text-text-secondary">Cancel</button></div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </details>
          )}
          {summary.qualityIssues?.length > 0 && (
            <details className="mt-4 border border-warning/30 bg-warning-muted p-3">
              <summary className="flex cursor-pointer items-center gap-2 text-sm font-medium text-warning"><IconAlertTriangle size={16} />{summary.qualityIssues.length} data issue{summary.qualityIssues.length === 1 ? '' : 's'}</summary>
              <ul className="mt-2 space-y-1 pl-6 text-sm text-text-secondary">{summary.qualityIssues.map((issue, index) => <li key={issue.code || index}>{issue.message || issue.code}</li>)}</ul>
            </details>
          )}
        </div>
      )}
    </section>
  );
}
